import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, RotateCw, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen photo viewer with wheel-zoom, drag-pan, +/- controls,
 * and optional prev/next navigation.
 *
 * Props:
 *  - photos: [{ src, caption }, ...]
 *  - index: number
 *  - onClose: () => void
 *  - onChangeIndex: (nextIndex) => void  (optional; enables arrows)
 */
export default function PhotoLightbox({ photos, index, onClose, onChangeIndex }) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const current = photos?.[index];
  const hasPrev = onChangeIndex && index > 0;
  const hasNext = onChangeIndex && index < (photos?.length ?? 0) - 1;

  const reset = useCallback(() => {
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reset zoom whenever we switch photos
  useEffect(() => {
    reset();
  }, [index, reset]);

  // Keyboard: Esc closes, arrows navigate, +/- zoom
  useEffect(() => {
    if (!current) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onChangeIndex(index - 1);
      else if (e.key === "ArrowRight" && hasNext) onChangeIndex(index + 1);
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(s * 1.25, 6));
      else if (e.key === "-" || e.key === "_") setScale((s) => Math.max(s / 1.25, 1));
      else if (e.key === "r" || e.key === "R") setRotation((r) => (r + 90) % 360);
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [current, hasPrev, hasNext, index, onChangeIndex, onClose, reset]);

  if (!current) return null;

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    setScale((s) => {
      const next = delta > 0 ? s * 1.1 : s / 1.1;
      return Math.min(Math.max(next, 1), 6);
    });
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = {
      startX: e.clientX - offset.x,
      startY: e.clientY - offset.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setOffset({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Non-fatal — pointer may already be released by browser
      if (process.env.NODE_ENV !== "production") console.debug("releasePointerCapture noop", err);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center select-none"
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Click backdrop to close */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-zoom-out"
        data-testid="lightbox-backdrop"
      />

      {/* Content */}
      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onWheel={onWheel}
      >
        <img
          src={current.src}
          alt={current.caption || "photo"}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transition: dragRef.current ? "none" : "transform 160ms ease-out",
            cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
            maxWidth: "90vw",
            maxHeight: "80vh",
          }}
          className="rounded-md shadow-2xl object-contain"
          data-testid="lightbox-img"
        />
      </div>

      {/* Top toolbar — positioned inside safe area so nothing clips */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1.5 sm:gap-2 z-20 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1.5">
        <ControlBtn onClick={() => setScale((s) => Math.min(s * 1.25, 6))} testid="lightbox-zoom-in" title="Zoom in (+)">
          <ZoomIn className="size-4" />
        </ControlBtn>
        <ControlBtn onClick={() => setScale((s) => Math.max(s / 1.25, 1))} testid="lightbox-zoom-out" title="Zoom out (-)">
          <ZoomOut className="size-4" />
        </ControlBtn>
        <ControlBtn onClick={() => setRotation((r) => (r - 90 + 360) % 360)} testid="lightbox-rotate-left" title="Rotate left">
          <RotateCcw className="size-4" />
        </ControlBtn>
        <ControlBtn onClick={() => setRotation((r) => (r + 90) % 360)} testid="lightbox-rotate-right" title="Rotate right (R)">
          <RotateCw className="size-4" />
        </ControlBtn>
        <ControlBtn onClick={reset} testid="lightbox-reset" title="Reset (0)">
          <span className="text-[10px] font-bold">1:1</span>
        </ControlBtn>
        <ControlBtn onClick={onClose} testid="lightbox-close" title="Close (Esc)">
          <X className="size-4" />
        </ControlBtn>
      </div>

      {/* Caption + counter */}
      {(current.caption || photos.length > 1) && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-center max-w-[90vw]">
          {current.caption && (
            <p className="text-white text-sm bg-black/40 px-3 py-1.5 rounded-md inline-block">
              {current.caption}
            </p>
          )}
          {photos.length > 1 && (
            <p className="text-white/70 text-xs mt-2" data-testid="lightbox-counter">
              {index + 1} / {photos.length}
            </p>
          )}
        </div>
      )}

      {/* Prev / Next arrows */}
      {hasPrev && (
        <button
          type="button"
          onClick={() => onChangeIndex(index - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 size-11 rounded-full bg-white/90 text-foreground grid place-items-center shadow hover:bg-white"
          data-testid="lightbox-prev"
          aria-label="Previous"
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={() => onChangeIndex(index + 1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 size-11 rounded-full bg-white/90 text-foreground grid place-items-center shadow hover:bg-white"
          data-testid="lightbox-next"
          aria-label="Next"
        >
          <ChevronRight className="size-5" />
        </button>
      )}
    </div>
  );
}

const ControlBtn = ({ children, onClick, testid, title }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    title={title}
    className="size-9 rounded-full bg-white/90 text-foreground grid place-items-center shadow hover:bg-white transition-colors"
  >
    {children}
  </button>
);
